import PropTypes from "prop-types"
import React, { useState } from "react"

import { formatDurationWithSessions } from "../../../utils/utils"

// Icons for the search bar
const TimeIcon = ({ theme, isMobile = false }) => (
  <svg className={`${isMobile ? "h-5 w-5" : "h-8 w-8"}`} viewBox="0 0 32 32" fill="none">
    <path
      d="M16 4C9.373 4 4 9.373 4 16s5.373 12 12 12 12-5.373 12-12S22.627 4 16 4zm0 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <path d="M16 8v8l6 6" stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"} strokeWidth="2" fill="none" />
  </svg>
)

const MapPinIcon = ({ theme, isMobile = false }) => (
  <svg className={`${isMobile ? "h-5 w-5" : "h-8 w-8"}`} viewBox="0 0 32 32" fill="none">
    <path
      d="M27 13.333C27 22 16 30 16 30S5 22 5 13.333C5 8.913 9.477 5.333 16 5.333s11 3.58 11 8z"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <circle
      cx="16"
      cy="13.333"
      r="3"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
  </svg>
)

const ChevronDownIcon = ({ theme, isMobile = false }) => (
  <svg className={`${isMobile ? "h-4 w-4" : "h-6 w-6"}`} viewBox="0 0 24 24" fill="none">
    <path
      d="M6 9l6 6 6-6"
      stroke={theme?.colors?.textDark || "#55433f"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ServiceTimeSearchBar = ({
  service,
  selectedDuration,
  selectedLocation,
  onDurationChange,
  onLocationChange,
  hasMultipleDurations,
  hasMultipleLocations,
  theme,
  deviceType = "desktop"
}) => {
  const [showDurationMenu, setShowDurationMenu] = useState(false)
  const [showLocationMenu, setShowLocationMenu] = useState(false)

  const formatPrice = (amountCents) => {
    if (!amountCents) return ""
    return `$${(amountCents / 100).toFixed(0)}`
  }

  const formatServiceDuration = (timeLength) => {
    if (!timeLength) return ""
    return formatDurationWithSessions(timeLength, service)
  }

  const formatLocation = (location) => {
    if (!location) return "Select location"

    if (location.kind === "virtual") {
      return "Virtual"
    } else if (location.kind === "home") {
      // For home locations, always show generic text (address is never shown for home)
      return "At your home"
    } else if (location.kind === "office") {
      // If hide_address/hideAddress is true, don't show address details
      // Check both snake_case and camelCase formats
      if (location.hide_address || location.hideAddress) {
        // Show location name if available, otherwise generic text
        return location.name || "In office"
      }

      // For office locations with visible address, show specific address information
      // Prefer displayAddress or singleLine from GraphQL if available
      if (location.displayAddress) {
        return location.displayAddress
      }
      if (location.singleLine) {
        return location.singleLine
      }

      // Build address from available fields
      const addressParts = []

      // Add location name if available (helps distinguish multiple locations)
      if (location.name) {
        addressParts.push(location.name)
      }

      // Add street address if available
      if (location.address) {
        let addr = location.address
        if (location.secondaryAddress) {
          addr += `, ${location.secondaryAddress}`
        }
        addressParts.push(addr)
      }

      // Add city and state
      if (location.city && location.stateAbbreviation) {
        addressParts.push(`${location.city}, ${location.stateAbbreviation}`)
      } else if (location.city && location.state) {
        addressParts.push(`${location.city}, ${location.state}`)
      }

      // Return formatted address or fallback to generic
      if (addressParts.length > 0) {
        return addressParts.join(", ")
      }

      // Last resort: generic text
      return "In office"
    } else {
      // For other location types (physical), check hide_address/hideAddress
      // Check both snake_case and camelCase formats
      if (location.hide_address || location.hideAddress) {
        return location.name || "Location"
      }

      // Try to show address
      if (location.displayAddress) {
        return location.displayAddress
      }
      if (location.singleLine) {
        return location.singleLine
      }
      return location.address || `${location.city}, ${location.stateAbbreviation}` || "Location"
    }
  }

  const handleDurationSelect = (duration) => {
    onDurationChange(duration)
    setShowDurationMenu(false)
  }

  const handleLocationSelect = (location) => {
    onLocationChange(location)
    setShowLocationMenu(false)
  }

  // Get duration options (main service + variations)
  const durationOptions = [
    {
      id: service.id,
      name: service.name,
      amountCents: service.amountCents,
      timeLength: service.timeLength
    },
    ...(service.variations || [])
  ]

  // Determine layout based on deviceType
  const isMobile = deviceType === "mobile"

  return (
    <div
      className={`box-border flex w-full gap-1.5 rounded-xl p-2 shadow-[0px_8px_9px_0px_rgba(0,0,0,0.07)] ${
        isMobile ? "flex-col" : "h-32 max-w-[872px] flex-row gap-4 p-6"
      }`}
      style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
      {/* Duration Selector */}
      <div className={`relative ${isMobile ? "w-full" : "w-1/2"}`}>
        <div
          className={`box-border flex w-full cursor-pointer items-center justify-start gap-1.5 border ${
            isMobile ? "rounded-md px-2 py-1" : "gap-4 rounded-xl px-5 py-3"
          }`}
          style={{
            borderColor: theme?.colors?.greyLight || "#e6e6e6",
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}
          onClick={() => hasMultipleDurations && setShowDurationMenu(!showDurationMenu)}>
          <div className={`flex flex-1 items-center justify-start ${isMobile ? "gap-1.5" : "gap-4"}`}>
            <TimeIcon theme={theme} isMobile={isMobile} />
            <div
              className="flex flex-1 flex-col items-start justify-start"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              <div
                className={`w-full font-semibold ${isMobile ? "text-xs leading-4" : "text-[20px] leading-[32px]"}`}
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                {selectedDuration ? formatServiceDuration(selectedDuration.timeLength) : "1 hour"}
              </div>
              <div
                className={`w-full font-normal ${isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"}`}
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                {selectedDuration ? formatPrice(selectedDuration.amountCents) : formatPrice(service.amountCents)}
              </div>
            </div>
          </div>
          {hasMultipleDurations && <ChevronDownIcon theme={theme} isMobile={isMobile} />}
        </div>

        {/* Duration Dropdown */}
        {showDurationMenu && hasMultipleDurations && (
          <div
            className={`absolute left-0 top-full z-10 mt-1 w-full border shadow-lg ${
              isMobile ? "rounded-md px-1 py-1.5" : "rounded-lg px-2 py-4"
            }`}
            style={{
              borderColor: theme?.colors?.greyLight || "#e6e6e6",
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            {durationOptions.map((duration) => (
              <div
                key={duration.id}
                className={`flex cursor-pointer items-center justify-start rounded ${
                  isMobile ? "gap-1 px-1.5 py-1" : "gap-4 px-4 py-2"
                } ${selectedDuration?.id === duration.id ? "bg-[#f8f6f5]" : "hover:bg-[#f8f6f5]"}`}
                onClick={() => handleDurationSelect(duration)}>
                <TimeIcon theme={theme} isMobile={isMobile} />
                <div
                  className="flex flex-1 flex-col items-start justify-start"
                  style={{ color: theme?.colors?.textDark || "#55433f" }}>
                  <div
                    className={`font-semibold ${isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"}`}
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {formatServiceDuration(duration.timeLength)}
                  </div>
                  <div
                    className={`font-normal ${isMobile ? "text-[8px]" : "text-[14px] leading-[20px]"}`}
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {formatPrice(duration.amountCents)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location Selector */}
      <div className={`relative ${isMobile ? "w-full" : "w-1/2"}`}>
        <div
          className={`box-border flex w-full cursor-pointer items-center justify-start gap-1.5 border ${
            isMobile ? "rounded-md px-2 py-1" : "gap-4 rounded-xl px-5 py-3"
          }`}
          style={{
            borderColor: theme?.colors?.greyLight || "#e6e6e6",
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}
          onClick={() => hasMultipleLocations && setShowLocationMenu(!showLocationMenu)}>
          <div className={`flex min-w-0 flex-1 items-center justify-start ${isMobile ? "gap-1.5" : "gap-4"}`}>
            <MapPinIcon theme={theme} isMobile={isMobile} />
            <div
              className="flex min-w-0 flex-1 flex-col items-start justify-start overflow-hidden"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              <div
                className={`w-full font-semibold ${isMobile ? "text-xs leading-4" : "text-[20px] leading-[32px]"}`}
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%"
                }}>
                {formatLocation(selectedLocation)}
              </div>
              <div
                className={`w-full truncate font-normal ${
                  isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"
                }`}
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                Location
              </div>
            </div>
          </div>
          {hasMultipleLocations && <ChevronDownIcon theme={theme} isMobile={isMobile} />}
        </div>

        {/* Location Dropdown */}
        {showLocationMenu && hasMultipleLocations && (
          <div
            className={`absolute left-0 top-full z-10 mt-1 w-full border shadow-lg ${
              isMobile ? "rounded-md px-1 py-1.5" : "rounded-lg px-2 py-4"
            }`}
            style={{
              borderColor: theme?.colors?.greyLight || "#e6e6e6",
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            {service.locations.map((location) => (
              <div
                key={location.id}
                className={`flex cursor-pointer items-center justify-start rounded ${
                  isMobile ? "gap-1 px-1.5 py-1" : "gap-4 px-4 py-2"
                } ${selectedLocation?.id === location.id ? "bg-[#f8f6f5]" : "hover:bg-[#f8f6f5]"}`}
                onClick={() => handleLocationSelect(location)}>
                <MapPinIcon theme={theme} isMobile={isMobile} />
                <div
                  className="flex flex-1 flex-col items-start justify-start"
                  style={{ color: theme?.colors?.textDark || "#55433f" }}>
                  <div
                    className={`w-full font-semibold ${
                      isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"
                    }`}
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      wordWrap: "break-word",
                      overflowWrap: "break-word"
                    }}>
                    {formatLocation(location)}
                  </div>
                  <div
                    className={`w-full font-normal ${isMobile ? "text-[8px]" : "text-[14px] leading-[20px]"}`}
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {location.kind === "virtual"
                      ? "Online"
                      : location.kind === "home"
                      ? "In-home"
                      : location.kind === "office"
                      ? "In-office"
                      : "In-person"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

ServiceTimeSearchBar.propTypes = {
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    variations: PropTypes.arrayOf(PropTypes.object),
    locations: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  selectedDuration: PropTypes.object,
  selectedLocation: PropTypes.object,
  onDurationChange: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  hasMultipleDurations: PropTypes.bool.isRequired,
  hasMultipleLocations: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  deviceType: PropTypes.string
}

export default ServiceTimeSearchBar
